import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { QrCode, CheckCircle2, Clock, ArrowRight, Camera, CameraOff } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import api, { formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { fmtDateTime, fmtDate } from "../../lib/dates";

function Status({ info, done, onCheckIn, busy }) {
  if (done || info.already_present) {
    return (
      <div className="rounded-2xl bg-palm/10 p-5 text-center" data-testid="checkin-done">
        <CheckCircle2 className="mx-auto text-palm" size={44} />
        <p className="mt-2 text-lg font-semibold text-palm">You're checked in!</p>
        <p className="text-sm text-muted-foreground">Recorded {fmtDateTime(done?.at || info.checked_at)}{(done?.method || info.method) === "qr" ? " via QR" : " by an officer"}. Akwaaba — enjoy the meeting.</p>
      </div>
    );
  }
  if (!info.open) {
    return (
      <div className="rounded-2xl border border-gold/40 bg-gold-light/50 p-5 text-center" data-testid="checkin-closed">
        <Clock className="mx-auto text-crimson" size={40} />
        <p className="mt-2 font-semibold text-crimson">Check-in is not open</p>
        <p className="text-sm text-muted-foreground">{info.reason === "opens later" && info.opens_at ? `Opens ${fmtDateTime(info.opens_at)} (Boston time).` : "This meeting's check-in window has ended."} An officer can still check you in manually.</p>
      </div>
    );
  }
  return <Button onClick={onCheckIn} disabled={busy} className="h-14 w-full rounded-full bg-crimson text-lg hover:bg-crimson-hover" data-testid="checkin-submit"><CheckCircle2 size={20} className="mr-2" /> {busy ? "Checking you in…" : "I'm here"}</Button>;
}

function ConsentPrompt({ onDone }) {
  const [busy, setBusy] = useState(false);
  const set = async (granted) => {
    setBusy(true);
    try { await api.put("/members/me/media-consent", { granted, note: "asked at meeting check-in" }); toast.success(granted ? "Thank you — noted" : "Noted — we won't publish photos of you"); onDone(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-gold/40 bg-parchment-card p-5" data-testid="checkin-consent-prompt">
      <div className="flex items-center gap-2 text-crimson"><Camera size={18} /><p className="font-semibold">One quick question — photos &amp; videos</p></div>
      <p className="mt-2 text-sm text-muted-foreground">We often take photos and videos at meetings and share them on the website, member portal and social media. Is it okay for us to publish ones that include you? You can change this any time in your profile.</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button onClick={() => set(true)} disabled={busy} className="rounded-full bg-palm hover:bg-palm/90" data-testid="checkin-consent-yes"><Camera size={16} className="mr-1" /> Yes, that's fine</Button>
        <Button onClick={() => set(false)} disabled={busy} variant="outline" className="rounded-full" data-testid="checkin-consent-no"><CameraOff size={16} className="mr-1" /> No thanks</Button>
      </div>
      <button onClick={onDone} className="mt-2 w-full text-center text-xs text-muted-foreground underline" data-testid="checkin-consent-later">Ask me later</button>
    </div>
  );
}

export default function CheckIn() {
  const { token } = useParams();
  const { data: info, error } = useApi(`/checkin/${token}`);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [consentDone, setConsentDone] = useState(false);
  const { refresh } = useAuth();
  const checkIn = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/checkin/${token}`); setDone(data); toast.success(data.status === "already" ? "You were already checked in" : "Checked in — Akwaaba!"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not check you in"); }
    finally { setBusy(false); }
  };
  if (error) return <div className="mx-auto max-w-md p-6 text-center" data-testid="checkin-invalid"><QrCode className="mx-auto text-crimson" size={40} /><p className="mt-3 font-semibold text-crimson">This check-in code isn't valid</p><p className="text-sm text-muted-foreground">{formatError(error.response?.data?.detail) || "Ask an officer for the current code."}</p><Link to="/portal" className="mt-4 inline-flex items-center gap-1 text-sm text-palm">Go to my dashboard <ArrowRight size={14} /></Link></div>;
  if (!info) return <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="mx-auto max-w-md space-y-5 p-6" data-testid="checkin-page">
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-gold">Meeting check-in</p>
        <h1 className="font-serif-display mt-1 text-2xl font-bold text-crimson" data-testid="checkin-event-title">{info.event.title}</h1>
        <p className="text-sm text-muted-foreground">{fmtDate(info.event.date)}{info.event.time ? ` · ${info.event.time}` : ""}{info.event.location ? ` · ${info.event.location}` : ""}</p>
      </div>
      <Status info={info} done={done} onCheckIn={checkIn} busy={busy} />
      {(done || info.already_present) && info.consent_unanswered && !consentDone && <ConsentPrompt onDone={() => { setConsentDone(true); refresh?.(); }} />}
      <Link to="/portal" className="block text-center text-sm text-palm underline" data-testid="checkin-back">Back to my dashboard</Link>
    </div>
  );
}
